import { useState, useEffect, useRef } from 'react'
import { serverApi } from '../api'
import { HiCpuChip, HiGlobeAlt, HiPlus, HiLockClosed, HiArrowPath, HiCheckCircle, HiXCircle } from 'react-icons/hi2'
import { useStore } from '../store'
import { Tabs } from '../components/UI'
import { Modal, ConfirmModal, Loader, Spinner, Badge, Dot } from '../ui'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import WebTerminal from '../components/WebTerminal'
import DeploymentHistory from '../components/DeploymentHistory'


// ── 2FA qulfi: server paneli root SSH beradi, shuning uchun oddiy login yetarli emas ──
function ServerUnlock({ onUnlocked }) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const submit = async () => {
    if (code.trim().length < 6) { setErr('6 xonali kodni kiriting'); return }
    setBusy(true); setErr(null)
    try {
      await serverApi.openSession(code.trim())
      onUnlocked()
    } catch (e) {
      setErr(e.message === 'SERVER_SESSION_REQUIRED' ? 'Kod qabul qilinmadi' : e.message)
    } finally { setBusy(false) }
  }

  return (
    <div className="page-enter" style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
      <div className="card" style={{ maxWidth: 460, width: '100%' }}>
        <div className="card-hdr" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <HiLockClosed size={20} />
          <h3 style={{ margin: 0 }}>Server paneli qulflangan</h3>
        </div>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0, lineHeight: 1.5 }}>
            Bu bo'lim serverga <b>root darajasida</b> kirish beradi. Shu sababli
            har safar telefoningizdagi <b>2FA kodi</b> so'raladi. Sessiya 15 daqiqadan
            so'ng avtomatik tugaydi.
          </p>
          <input
            autoFocus
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={e => e.key === 'Enter' && submit()}
            style={{
              width: '100%', padding: '12px 16px', borderRadius: 6,
              border: '1px solid var(--ui-border)', fontSize: 18, letterSpacing: 8,
              textAlign: 'center', background: 'var(--ui-surface)',
              fontFamily: 'var(--ui-mono)', fontVariantNumeric: 'tabular-nums'
            }}
          />
          {err && <div style={{ color: 'var(--ui-st-critical)', fontSize: 13 }}>{err}</div>}
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Tekshirilmoqda...' : 'Ochish'}
          </button>
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
            2FA yoqilmagan bo'lsa: Profil → Xavfsizlik → 2FA ni yoqing.
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ServerManager() {
  const [unlocked, setUnlocked] = useState(() => serverApi.hasSession())
  const [commits, setCommits] = useState([])
  const [domains, setDomains] = useState([])
  const [logs, setLogs] = useState('')
  const [metrics, setMetrics] = useState([])
  const [activeContainer, setActiveContainer] = useState('crt-backend')
  const [loading, setLoading] = useState(true)
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [error, setError] = useState(null)
  const logsContainerRef = useRef(null)

  
  const [showAddModal, setShowAddModal] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [targetPort, setTargetPort] = useState('')
  const [addingDomain, setAddingDomain] = useState(false)
  
  const [activeTab, setActiveTab] = useState('domains')

  useEffect(() => {
    // Kichik kechikish bilan scroll qilish (DOM to'liq render bo'lishi uchun)
    setTimeout(() => {
      if (logsContainerRef.current) {
        logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight
      }
    }, 50)
  }, [logs, activeTab])
  
  const [issuingSsl, setIssuingSsl] = useState(null)
  // Qaysi domen uchun SSL so'ralayotgani tasdiq oynasida saqlanadi (window.confirm o'rniga)
  const [sslTarget, setSslTarget] = useState(null)
  const { addToast } = useStore()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [cRes, dRes, lRes, mRes] = await Promise.all([
        serverApi.getCommits(5),
        serverApi.getDomains(),
        serverApi.getLogs(activeContainer),
        serverApi.getMetrics()
      ])
      setCommits(cRes.commits || [])
      setDomains(dRes.domains || [])
      setLogs(lRes.logs || '')
      setMetrics(mRes.metrics || [])
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchLogs = async (container) => {
    setActiveContainer(container)
    setLoadingLogs(true)
    try {
      const res = await serverApi.getLogs(container)
      setLogs(res.logs || '')
    } catch (err) {
      addToast('Loglarni yuklashda xato', 'error')
    } finally {
      setLoadingLogs(false)
    }
  }

  const handleAddDomain = async (e) => {
    e.preventDefault()
    if (!newDomain || !targetPort) return
    setAddingDomain(true)
    try {
      await serverApi.addDomain(newDomain, targetPort)
      addToast('Domen muvaffaqiyatli qo\'shildi', 'default')
      setShowAddModal(false)
      setNewDomain('')
      setTargetPort('')
      await loadData()
    } catch (err) {
      addToast(err.message || 'Domen qo\'shilmadi', 'critical')
    } finally {
      setAddingDomain(false)
    }
  }

  const handleIssueSsl = async (domain) => {
    if (!domain) return
    setSslTarget(null)
    setIssuingSsl(domain)
    try {
      await serverApi.issueSsl(domain)
      addToast(`${domain} uchun SSL muvaffaqiyatli olindi`, 'default')
      await loadData()
    } catch (err) {
      addToast(`${domain} uchun SSL olinmadi: ${err.message}`, 'critical')
    } finally {
      setIssuingSsl(null)
    }
  }

  const TABS = [
    { id: 'stats', label: 'Statistika' },
    { id: 'domains', label: 'Domenlar' },
    { id: 'commits', label: 'CI/CD Tarixi' },
    { id: 'logs', label: 'Server Logs' },
    { id: 'terminal', label: 'Terminal' }
  ]

  if (!unlocked) return <ServerUnlock onUnlocked={() => { setUnlocked(true); window.location.reload() }} />

  return (
    <div className="page-enter">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <h2>Server boshqaruvi</h2>
          <p>Tizimning asosiy sozlamalari va jurnali</p>
        </div>
        <div className="page-hdr-right" style={{ display: 'flex', gap: 12 }}>
          {activeTab === 'domains' && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <HiPlus size={16} /> Domen qo'shish
            </button>
          )}
          <button className="btn-refresh-icon" onClick={loadData} title="Yangilash" aria-label="Ma'lumotlarni yangilash">
            {loading ? <Spinner size={16} /> : <HiArrowPath size={16} aria-hidden="true" />}
          </button>
        </div>
      </div>

      <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {error && <div className="alert alert-error mb-4" style={{ borderRadius: 8 }}>{error}</div>}

      <div style={{ marginTop: 24 }}>
        {activeTab === 'stats' && (
          <div className="card" style={{ padding: 24, borderRadius: 8, border: '1px solid var(--ui-border)', background: 'var(--ui-surface)' }}>
            <h3 style={{ margin: '0 0 24px', fontSize: 18, fontWeight: 600, color: 'var(--ui-text-1)' }}>So'nggi 24 soatlik so'rovlar</h3>
            <div style={{ height: 350, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={metrics} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                  <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: 'var(--text-3)' }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: 'var(--text-3)' }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid var(--ui-border)', boxShadow: 'var(--ui-shadow-pop)', background: 'var(--ui-surface)' }}
                    itemStyle={{ color: 'var(--ui-text-1)', fontWeight: 600 }}
                  />
                  <Line type="monotone" dataKey="requests" name="So'rovlar" stroke="var(--ui-text-1)" strokeWidth={2} dot={{ r: 3, strokeWidth: 1, fill: 'var(--ui-surface)', stroke: 'var(--ui-text-1)' }} activeDot={{ r: 5, strokeWidth: 0, fill: 'var(--ui-text-1)' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {activeTab === 'domains' && (
          <div className="card" style={{ padding: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--ui-border)' }}>
            <div className="table-responsive">
              <table className="tbl" style={{ width: '100%' }}>
                <thead>
                  <tr style={{ background: 'var(--surface)' }}>
                    <th style={{ paddingLeft: 28 }}>Domen</th>
                    <th>Target (Proxy)</th>
                    <th>Holati</th>
                    <th style={{ paddingRight: 28, textAlign: 'right' }}>Amallar</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && domains.length === 0 ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <tr key={`sk-${i}`}>
                        <td style={{ paddingLeft: 28 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div className="skeleton" style={{ width: 8, height: 8, borderRadius: '50%' }} />
                            <div className="skeleton" style={{ width: 120, height: 16 }} />
                          </div>
                          <div className="skeleton" style={{ width: 160, height: 12, marginLeft: 20, marginTop: 8 }} />
                        </td>
                        <td><div className="skeleton" style={{ width: 100, height: 26, borderRadius: 6 }} /></td>
                        <td><div className="skeleton" style={{ width: 80, height: 26, borderRadius: 6 }} /></td>
                        <td style={{ paddingRight: 28, textAlign: 'right' }}><div className="skeleton" style={{ width: 90, height: 32, borderRadius: 6, display: 'inline-block' }} /></td>
                      </tr>
                    ))
                  ) : (
                    domains.map((d, i) => (
                      <tr key={i} style={{ transition: 'background 0.2s' }}>
                        <td style={{ paddingLeft: 28 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Dot tone={d.ssl_enabled ? 'ok' : 'busy'} label={d.ssl_enabled ? 'SSL faol' : 'SSL yo\'q'} />
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{d.domain}</span>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--ui-text-3)', marginLeft: 20, marginTop: 4, fontFamily: 'var(--ui-mono)' }}>{d.config_name}</div>
                        </td>
                        <td>
                          <span style={{
                            background: 'var(--ui-surface-2)', padding: '4px 8px', borderRadius: 6,
                            border: '1px solid var(--ui-border)', fontFamily: 'var(--ui-mono)', fontSize: 12, color: 'var(--ui-text-2)'
                          }}>
                            {d.proxy_target ? `localhost:${d.proxy_target}` : 'N/A'}
                          </span>
                        </td>
                        <td>
                          {d.ssl_enabled ? (
                            <Badge tone="ok" icon={HiCheckCircle}>SSL Faol</Badge>
                          ) : (
                            <Badge tone="high" icon={HiXCircle}>SSL Yo'q</Badge>
                          )}
                        </td>
                        <td style={{ paddingRight: 28, textAlign: 'right' }}>
                          {!d.ssl_enabled && (
                            <button 
                              className="btn btn-sm" 
                              onClick={() => setSslTarget(d.domain)}
                              disabled={issuingSsl === d.domain}
                              style={{
                                background: 'var(--ui-surface)',
                                border: '1px solid var(--ui-border-2)',
                                color: 'var(--ui-text-1)',
                                fontWeight: 600,
                                padding: '4px 8px'
                              }}
                            >
                              {issuingSsl === d.domain ? <Spinner size={16} /> : <HiLockClosed size={16} aria-hidden="true" />}
                              <span style={{ marginLeft: 4 }}>SSL Olish</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                  {!loading && domains.length === 0 && (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', padding: 48, color: 'var(--ui-text-3)' }}>
                        Domenlar topilmadi
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'commits' && <DeploymentHistory />}

        {activeTab === 'logs' && (
          <div className="card" style={{ padding: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--ui-border)', background: 'var(--ui-surface)' }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--ui-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--ui-surface-2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--ui-surface-3)', color: 'var(--ui-text-1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <HiCpuChip size={20} aria-hidden="true" />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--ui-text-1)' }}>Server Logs</h3>
                  <div style={{ fontSize: 12, color: 'var(--ui-text-3)' }}>Tizim jurnali va xatoliklar</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {['crt-backend', 'crt-frontend'].map(c => (
                  <button
                    key={c}
                    onClick={() => fetchLogs(c)}
                    aria-pressed={activeContainer === c}
                    style={{
                      background: activeContainer === c ? 'var(--ui-text-1)' : 'var(--ui-surface)',
                      color: activeContainer === c ? 'var(--ui-surface)' : 'var(--ui-text-2)',
                      border: `1px solid ${activeContainer === c ? 'var(--ui-text-1)' : 'var(--ui-border)'}`,
                      padding: '4px 12px',
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'background var(--ui-dur) var(--ui-ease), border-color var(--ui-dur) var(--ui-ease)'
                    }}
                  >
                    {c}
                  </button>
                ))}
                <button
                  onClick={() => fetchLogs(activeContainer)}
                  style={{
                    background: 'var(--ui-surface)',
                    color: 'var(--ui-text-2)',
                    border: '1px solid var(--ui-border)',
                    padding: '4px 8px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Yangilash"
                  aria-label="Loglarni yangilash"
                >
                  {loadingLogs ? <Spinner size={16} /> : <HiArrowPath size={16} aria-hidden="true" />}
                </button>
              </div>
            </div>
            <div ref={logsContainerRef} style={{ padding: 24, height: 600, overflowY: 'auto', fontFamily: 'var(--ui-mono)', fontSize: 12, color: 'var(--ui-text-1)', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'var(--ui-surface)' }}>
              {loadingLogs ? (
                <div style={{ color: 'var(--ui-text-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Loader state="working" size="lg" block />
                </div>
              ) : logs ? (
                <div style={{ background: 'var(--ui-surface-2)', padding: 16, borderRadius: 6, border: '1px solid var(--ui-border)', minHeight: '100%' }}>
                  {logs}
                </div>
              ) : (
                <div style={{ color: 'var(--ui-text-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>Loglar bo'sh</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'terminal' && (
          <div style={{ marginTop: 24 }}>
            <WebTerminal />
          </div>
        )}
      </div>

      {/* ADD DOMAIN MODAL — kit Modal: role="dialog", aria-modal, Esc, fokus qopqoni */}
      <Modal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Domen qo'shish"
        size="sm"
      >
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--ui-text-3)' }}>
          Nginx orqali yangi proksi ulanishini yarating
        </p>
        <form onSubmit={handleAddDomain}>
          <div className="form-group mb-20">
            <label htmlFor="sm-domain" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ui-text-2)', marginBottom: 8, display: 'block' }}>
              Domen nomi
            </label>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ui-text-3)', display: 'flex' }}>
                <HiGlobeAlt size={16} aria-hidden="true" />
              </div>
              <input
                id="sm-domain"
                type="text"
                className="form-input"
                style={{ paddingLeft: 36, height: 32, fontSize: 13 }}
                value={newDomain}
                onChange={e => setNewDomain(e.target.value)}
                placeholder="masalan: api.cyberadvice.uz"
                required
              />
            </div>
          </div>

          <div className="form-group mb-24">
            <label htmlFor="sm-port" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ui-text-2)', marginBottom: 8, display: 'block' }}>
              Ichki port (Target Port)
            </label>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ui-text-3)', display: 'flex' }}>
                <HiCpuChip size={16} aria-hidden="true" />
              </div>
              <input
                id="sm-port"
                type="number"
                className="form-input"
                style={{ paddingLeft: 36, height: 32, fontSize: 13 }}
                value={targetPort}
                onChange={e => setTargetPort(e.target.value)}
                placeholder="masalan: 7703"
                required
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid var(--ui-border)' }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Bekor qilish</button>
            <button type="submit" className="btn btn-primary" disabled={addingDomain}>
              {addingDomain ? 'Ulanmoqda...' : 'Ulash'}
            </button>
          </div>
        </form>
      </Modal>

      {/* SSL TASDIQI — xavfli emas, lekin Let's Encrypt ga tashqi so'rov yuboradi */}
      <ConfirmModal
        open={!!sslTarget}
        onClose={() => setSslTarget(null)}
        onConfirm={() => handleIssueSsl(sslTarget)}
        title="SSL sertifikat olish"
        text={`${sslTarget || ''} uchun Let's Encrypt dan sertifikat so'raladi. Let's Encrypt bir domenga haftasiga cheklangan sondagi so'rovni qabul qiladi, shuning uchun bekorga takrorlamang.`}
        confirmLabel="Sertifikat olish"
      />
    </div>
  )
}
